"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { UniversalPageHero, ProcessSteps } from "@/components/common";
import { getServiceRoute } from "@/lib/serviceUtils";

// Service images from public directory
const aisImage = "/images/services/ais.jpg";
const sdsImage = "/images/services/sds.jpg";
const sisImage = "/images/services/sis.jpg";
const dtcImage = "/images/services/dtc.jpg";
const cadiImage = "/images/services/cadi.jpg";
const hspImage = "/images/services/hsp.jpg";
const pmImage = "/images/services/pm.jpg";
const asImage = "/images/services/as.jpg";
const smImage = "/images/services/sm.jpg";

interface ServiceInfo {
  id: string;
  title: string;
  description: string;
  image: string;
}

const services: ServiceInfo[] = [
  {
    id: "ais",
    title: "AI Solutions & Integration",
    description:
      "We specialize in developing cutting-edge AI solutions powered by Large Language Models (LLMs) to transform your business operations. Our tailored AI implementations range from intelligent chatbots and knowledge management systems to advanced detection and analytics solutions, helping organizations leverage the power of artificial intelligence for enhanced productivity and innovation.",
    image: aisImage,
  },
  {
    id: "sds",
    title: "Software Development and Support",
    description:
      "We design, develop, and maintain custom software solutions, including mobile and web applications, tailored to meet the unique needs of businesses across various industries. Our development team ensures seamless functionality, scalability, and user-friendly interfaces.",
    image: sdsImage,
  },
  {
    id: "sis",
    title: "System Integration and Support",
    description:
      "We provide seamless integration of diverse systems and platforms, enhancing interoperability and operational efficiency. Our support services ensure continued reliability and peak performance.",
    image: sisImage,
  },
  {
    id: "dtc",
    title: "Digital Transformation and Consultation",
    description:
      "Empowering organizations to embrace digital innovation, we deliver tailored strategies and solutions to optimize processes, leverage emerging technologies, and achieve transformative business outcomes.",
    image: dtcImage,
  },
  {
    id: "cadi",
    title: "Cloud Architecture Design and Implementation",
    description:
      "Our cloud experts design, implement, and manage secure, scalable cloud infrastructures, ensuring optimal performance, cost efficiency, and alignment with business objectives.",
    image: cadiImage,
  },
  {
    id: "hsp",
    title: "Hardware & Software Provisioning",
    description:
      "We source and deploy state-of-the-art hardware and software solutions, offering businesses the tools they need to stay ahead in a competitive environment.",
    image: hspImage,
  },
  {
    id: "pm",
    title: "Product Management",
    description:
      "We provide strategic oversight for product lifecycles, focusing on market analysis, roadmap planning, and aligning products with business goals to maximize customer satisfaction and revenue growth.",
    image: pmImage,
  },
  {
    id: "as",
    title: "AI Solutions",
    description:
      "Leveraging artificial intelligence, we deliver intelligent solutions for automation, predictive analytics, and advanced decision-making, transforming business operations and customer engagement.",
    image: asImage,
  },
  {
    id: "sm",
    title: "Smart Manufacturing",
    description:
      "We empower manufacturers with cutting-edge Industry 4.0 solutions, integrating IoT, AI, and automation to enhance productivity, quality, and operational efficiency.",
    image: smImage,
  },
];

export default function ServiceDetails() {
  const router = useRouter();
  const [activeService, setActiveService] = useState<ServiceInfo>(services[0]);

  useEffect(() => {
    const syncActiveServiceFromHash = () => {
      const hash = window.location.hash.substring(1);
      const service = services.find((item) => item.id === hash) ?? services[0];
      setActiveService(service);
    };

    syncActiveServiceFromHash();
    window.addEventListener("hashchange", syncActiveServiceFromHash);

    return () => {
      window.removeEventListener("hashchange", syncActiveServiceFromHash);
    };
  }, []);

  const handleServiceClick = (serviceId: string) => {
    window.location.hash = serviceId;
    const service = services.find((item) => item.id === serviceId);
    if (service) {
      setActiveService(service);
    }
  };

  return (
    <div>
      <UniversalPageHero
        title={activeService.title}
        breadcrumb="Services / Service Details"
        variant="gradient"
      />
      
      <div className="py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="overflow-hidden rounded-lg bg-card shadow-md">
              {services.map((service) => (
                <div
                  key={service.id}
                  onClick={() => {
                    // Use the centralized utility function for consistent service routes
                    const route = getServiceRoute(service.id);

                    // If the route isn't to the generic service page, navigate to the specific service page
                    if (route !== "/service") {
                      router.push(route);
                    } else {
                      // Otherwise, use the hash-based navigation within this page
                      handleServiceClick(service.id);
                    }
                  }}
                  className={`flex cursor-pointer items-center border-b border-border p-4 transition-colors hover:bg-primary-subtle ${
                    activeService.id === service.id
                      ? "bg-primary-subtle text-primary font-medium"
                      : ""
                  }`}
                >
                  <div className="mr-2">
                    {activeService.id === service.id ? (
                      <i className="fas fa-chevron-right text-primary"></i>
                    ) : (
                      <i className="fas fa-angle-right text-neutral-muted"></i>
                    )}
                  </div>
                  <span>{service.title}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Main Content */}
          <div className="md:col-span-2">
            <div>
              <div className="rounded-lg overflow-hidden shadow-md mb-8">
                <img
                  src={activeService.image}
                  alt={activeService.title}
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={400}
                  className="w-full h-[400px] object-cover rounded-lg"
                />
              </div>

              <h2 className="mb-4 text-2xl font-bold text-neutral-emphasis">
                {activeService.title}
              </h2>
              <p className="mb-6 text-neutral-muted">
                {activeService.description}
              </p>

              <div className="mt-8">
                <h3 className="mb-4 text-xl font-bold text-neutral-emphasis">
                  Key Benefits
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 rounded-full bg-primary-surface p-2">
                      <i className="fas fa-check text-primary"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-neutral-emphasis">Tailored Solutions</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 rounded-full bg-primary-surface p-2">
                      <i className="fas fa-check text-primary"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-neutral-emphasis">
                        Expert Implementation
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 rounded-full bg-primary-surface p-2">
                      <i className="fas fa-check text-primary"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-neutral-emphasis">Ongoing Support</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 rounded-full bg-primary-surface p-2">
                      <i className="fas fa-check text-primary"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-neutral-emphasis">Measurable Results</p>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="mb-6 text-xl font-bold text-neutral-emphasis">
                    Our Approach
                  </h3>
                  <p className="mb-6 text-neutral-muted">
                    We follow a structured approach to deliver high-quality
                    solutions that meet your unique business requirements:
                  </p>
                  <ProcessSteps
                    variant="compact"
                    steps={[
                      {
                        title: "In-depth Consultation",
                        description:
                          "Understand your specific needs and challenges through comprehensive analysis",
                      },
                      {
                        title: "Collaborative Strategy Development",
                        description:
                          "Work closely with your team to develop tailored solutions",
                      },
                      {
                        title: "Agile Implementation",
                        description:
                          "Execute with regular check-ins and updates throughout the process",
                      },
                      {
                        title: "Testing & Quality Assurance",
                        description:
                          "Ensure reliability and performance through thorough testing",
                      },
                      {
                        title: "Training & Documentation",
                        description:
                          "Provide comprehensive training and detailed documentation",
                      },
                      {
                        title: "Ongoing Support",
                        description:
                          "Continuous support and optimization for long-term success",
                      },
                    ]}
                  />
                </div>

                <div className="text-center">
                  <Button
                    onClick={() => router.push("/contact")}
                    className="bg-primary px-6 py-3 font-bold text-primary-foreground hover:bg-primary-strong"
                  >
                    Request a Consultation
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
